<template>
  <v-card flat>
    <v-card-title>Create New Document</v-card-title>
    <v-card-text>
      <v-card outlined>
        <v-card-title class="text-subtitle-2 grey lighten-4 text-break">
          Summary
        </v-card-title>
        <v-card-text class="mt-4">
          <v-form ref="createDocumentForm" lazy-validation>
            <v-row>
              <v-col cols="12" sm="6" :class="$vuetify.breakpoint.xsOnly ? '' : 'pr-2'">
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-textarea
                        v-if="isFieldVisible(DocumentFields.Subject)"
                        class="required"
                        v-model="esv.subject"
                        :rules="getFieldRules(DocumentFields.Subject)"
                        label="Subject"
                        outlined
                        dense
                        hide-details="auto"
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" :class="$vuetify.breakpoint.xsOnly ? 'pb-4 pt-0' : 'pb-4 pl-2'">
                <v-row no-gutters>
                  <v-col cols="12" sm="4" :class="$vuetify.breakpoint.xsOnly ? 'pb-4' : 'pb-4 pr-4'">
                    <v-autocomplete
                        v-model="esv.serviceRequestTypeId"
                        :items="serviceRequestTypes"
                        class="required"
                        label="Service Request Type"
                        :rules="[$common.rules.required]"
                        hide-details="auto"
                        outlined
                        dense
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="4" :class="$vuetify.breakpoint.xsOnly ? 'pb-4' : 'pb-4 pr-4'">
                    <v-text-field
                        class="required"
                        v-model="esv.dossier"
                        :disabled="$common.isRns(esv)"
                        :label="dossierLabel"
                        :rules="getFieldRules('dossier')"
                        hide-details="auto"
                        outlined
                        dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4" class="pb-4">
                    <v-text-field
                        v-if="isFieldVisible(DocumentFields.InputCreateVersion)"
                        class="required"
                        v-model="esv.version"
                        label="Version"
                        :rules="getFieldRules(DocumentFields.InputCreateVersion)"
                        hide-details="auto"
                        outlined
                        dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" class="pb-4">
                    <date-picker
                        required
                        label="Issue date"
                        v-model="esv.issueDate"
                        :rules="getFieldRules(DocumentFields.IssueDate)"
                        @input="(value) => (esv.issueDate = value)"
                    ></date-picker>
                  </v-col>
                  <v-col cols="12" class="pb-4" v-if="isFieldVisible(DocumentFields.InputCreateServiceLimitTime)">
                    <date-picker
                        v-if="isFieldVisible(DocumentFields.InputCreateServiceLimitTime)"
                        dotted
                        label="Service limit time"
                        v-model="esv.serviceLimitTime"
                        :rules="getFieldRules(DocumentFields.InputCreateServiceLimitTime)"
                        @input="(value) => {(esv.serviceLimitTime = value); sharedRulesUpdate(); }"
                    ></date-picker>
                  </v-col>
                  <v-col cols="12" class="pb-3" v-if="isFieldVisible(DocumentFields.InputCreateServicePeriod)">
                    <v-text-field
                        v-if="isFieldVisible(DocumentFields.InputCreateServicePeriod)"
                        v-model="esv.servicePeriod"
                        label="Service period in years"
                        :rules="getFieldRules(DocumentFields.InputCreateServicePeriod)"
                        @input="sharedRulesUpdate()"
                        hide-details="auto"
                        class="dotted"
                        clearable
                        outlined
                        dense
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider></v-divider>
            <v-row class="mt-4 mb-4" no-gutters>
              <v-col cols="12">
                <v-select
                    v-if="isFieldVisible(DocumentFields.InputCreateServiceGrade)"
                    class="required"
                    v-model="esv.serviceGradeId"
                    label="Service Grade"
                    :items="serviceGrades"
                    :rules="getFieldRules(DocumentFields.InputCreateServiceGrade)"
                    outlined
                    dense
                    hide-details="auto"
                ></v-select>
              </v-col>
            </v-row>
            <v-divider></v-divider>
            <v-row class="mt-4 mb-4" no-gutters>
              <v-col cols="12">
                <v-select
                    class="required"
                    v-if="$common.isRns(esv)"
                    v-model="esv.confidentiality"
                    label="Confidentiality"
                    :items="confidentialityOptions"
                    :rules="getFieldRules(DocumentFields.Confidentiality)"
                    outlined
                    dense
                    hide-details="auto"
                ></v-select>
                <v-select
                    v-else
                    v-model="esv.confidentiality"
                    :items="internalUseOnlyOption"
                    label="Confidentiality"
                    outlined
                    dense
                    hide-details="auto"
                    disabled
                ></v-select>
              </v-col>
            </v-row>
            <v-divider></v-divider>

            <v-row class="mt-4" no-gutters>
              <v-col cols="6" class="pr-2">
                <v-select
                    v-if="isFieldVisible(DocumentFields.ProductGroup)"
                    v-model="esv.productGroupId"
                    :items="productGroups"
                    label="Product group"
                    :rules="getFieldRules(DocumentFields.ProductGroup)"
                    hide-details="auto"
                    clearable
                    outlined
                    dense
                ></v-select>
              </v-col>
              <v-col cols="6" class="pl-2">
                <v-select
                    v-if="isFieldVisible(DocumentFields.ProductSubgroup)"
                    v-model="esv.productSubGroupId"
                    :items="productSubGroups"
                    label="Product subgroup"
                    :rules="getFieldRules(DocumentFields.ProductSubgroup)"
                    hide-details="auto"
                    clearable
                    outlined
                    dense
                    no-data-text="Select a product group"
                ></v-select>
              </v-col>
            </v-row>

            <v-textarea
                v-if="isFieldVisible(DocumentFields.PublicMessage)"
                class="pb-0 pt-4"
                v-model="esv.publicMessage"
                label="Public message"
                :rules="getFieldRules(DocumentFields.PublicMessage)"
                placeholder="Briefly describe the problem and solution in a less technical way in order to make it understandable for end users and sales people."
              hide-details="auto"
                auto-grow
                outlined
                dense
            ></v-textarea>
          </v-form>
        </v-card-text>
      </v-card>

      <floating-button @click="postNewDocument" :loading="loading">
        Create as new document
      </floating-button>
    </v-card-text>
  </v-card>
</template>

<script>
// @ is an alias to /src
import DatePicker from "@/components/common/DatePicker.vue";
import FloatingButton from "@/components/common/FloatingButton.vue";
import {DocumentFields} from "@/enums/DocumentFields";

export default {
  name: "Create",
  components: {
    DatePicker,
    FloatingButton,
  },
  data() {
    let esv = {
      confidentiality: 1,
      serviceRequestTypeId: null,
      serviceGradeId: null
    };
    return {
      esv: esv,
      loading: false,

      serviceGrades: [],
      serviceRequestTypes: [],
      productGroups: [],
      confidentialityOptions: [
        {value: 1, text: 'For internal use only'},
        {value: 2, text: 'Public'},
      ],
      internalUseOnlyOption: [
        {value: 1, text: 'For internal use only'},
      ],

      serviceLimitTimeMenu: false,
      sharedRules: () => {
        let errorServicePeriod =
            esv.servicePeriod === undefined ||
            esv.servicePeriod === "" ||
            esv.servicePeriod === null;
        let errorServiceLimitTime =
            esv.serviceLimitTime === undefined || esv.serviceLimitTime === null;
        let error = !errorServicePeriod || !errorServiceLimitTime;

        return (
            error || "Please fill in 'Service period' OR 'Service limit time'"
        );
      },
    };
  },
  methods: {
    isFieldVisible(fieldName) {
      return this.$common.isFieldVisible(this.$store, this.esv, fieldName);
    },
    getServiceGrades() {
      this.$api
          .getServiceGrades()
          .then((resp) => (this.serviceGrades = resp.data));
    },
    getServiceRequestTypes() {
      this.$api
          .getServiceRequestTypes()
          .then((resp) => (this.serviceRequestTypes = resp.data));
    },
    getProductGroupProductSubGroups() {
      this.$api
          .getProductGroupProductSubGroups()
          .then((resp) => (this.productGroups = resp.data));
    },

    postNewDocument() {
      if (this.$refs.createDocumentForm.validate()) {
        this.loading = true;
        if (this.esv.serviceRequestTypeId === 24) {
          this.esv.dossier = "1111111";
          this.esv.serviceGradeId = "0";
        }

        this.$api
            .postNewDocument(this.esv)
            .then((resp) => {
              this.$toast.success("Document has been created");
              this.$router.push({name: "ESV", params: {esvId: resp.data}});
            })
            .catch((error) => {
              console.error('Error creating document:', error);
            })
            .finally(() => (this.loading = false));
      }
    },
    sharedRulesUpdate() {
      this.$refs.createDocumentForm.validate();
    }
  },
  watch: {
    'esv.serviceRequestTypeId'(newValue) {
      this.$set(this.esv, 'dossier', '');
      this.$set(this.esv, 'version', '');

      if (newValue === 24) {
        this.$toast.info('The File No. (Dossier) will be generated automatically.');
        this.esv.version = "99";
      }
    },
  },
  computed: {
    getFieldRules() {
      return (fieldName) => {
        if (!this.isFieldVisible(fieldName)) {
          return [];
        }

        // Return appropriate rules based on field
        switch (fieldName) {
          case DocumentFields.Subject:
            return [this.$common.rules.required];
            
          case "dossier":
            if (this.isFieldVisible(DocumentFields.InputCreateVersion)) {
              return [this.$common.rules.required, this.$common.rules.dossier];
            }
            break;
          
          case DocumentFields.InputCreateServiceGrade:
            return [this.$common.rules.required];

          case DocumentFields.InputCreateVersion:
            return [this.$common.rules.required, this.$common.rules.integer];

            // Special case for service period/limit time
          case DocumentFields.InputCreateServiceLimitTime:
            return [this.sharedRules];

          case DocumentFields.InputCreateServicePeriod:
            return [this.sharedRules, this.$common.rules.decimal];

          default:
            return [];
        }
      };
    },
    DocumentFields() {
      return DocumentFields
    },
    dossierLabel() {
      if (this.esv.serviceRequestTypeId === 24) {
        return 'Dossier (Auto-Generated)';
      }
      return 'Dossier';
    },
    productSubGroups() {
      if (this.esv.productGroupId) {
        return this.productGroups.find(
            (pg) => pg.value == this.esv.productGroupId
        ).productSubGroups;
      } else {
        return [];
      }
    },
  },
  created() {
    this.getServiceGrades();
    this.getServiceRequestTypes();
    this.getProductGroupProductSubGroups();
  },
};
</script>

<style scoped>
.required >>> fieldset {
  border-left: 3px solid dodgerblue;
}

.dotted >>> fieldset {
  border-left: 3px dotted dodgerblue;
}
</style>